import React, {
  ComponentProps,
  ForwardedRef,
  MutableRefObject,
  useRef,
} from "react";
import MapGL from "react-map-gl";
import { buildLoginURLWithPrevLocation } from "../../../utils/params";
import { Search, SearchResultProps } from "../../Search";
import { Viewport } from "../utils/viewportHook";
import { SearchContainer } from "./__styles__/LayeredMap";
import useAccountBoundaryHook from "./accountBoundaryLayer";
import useCbrsHook from "./cbrsLayer";
import useClickObjectMenuHook from "./clickObjectMenuLayer";
import useContourHook from "./contourLayer";
import CustomMapController from "./CustomMapController";
import useCustomMapHook from "./customMapLayer";
import useFirmHook from "./firmLayer";
import useLayeredMapHook from "./layeredMapHook";
import useMarkerHook from "./markerLayer";
import useMeasureHook from "./measureLayer";
import useParcelHook from "./parcelLayer";
import usePropertyHook from "./propertyLayer";
import useRasterHook from "./rasterLayer";
import useSavedViewsClusterHook from "./savedViewsClusterLayer";
import useSavedViewsHook from "./savedViewsLayer";
import TooltipComponent from "./Tooltip";
import type {
  Account,
  CurriedHookBuilder,
  LayerConfigs,
  LayerHookBuilder,
  MapRef,
  ParentLayerNames,
} from "./types";
import useDeviceLocationsHook from "./userDeviceLocationsLayer";

const layerHookBuilders = {
  parcels: useParcelHook,
  marker: useMarkerHook,
  accountBoundary: useAccountBoundaryHook,
  properties: usePropertyHook,
  firms: useFirmHook,
  customMaps: useCustomMapHook,
  savedViews: useSavedViewsHook,
  savedViewsCluster: useSavedViewsClusterHook,
  cbrs: useCbrsHook,
  contour: useContourHook,
  measure: useMeasureHook,
  raster: useRasterHook,
  deviceLocations: useDeviceLocationsHook,
  clickObjectMenu: useClickObjectMenuHook,
};

type Props = {
  layers: LayerConfigs;
  onSearchResult?: (result: SearchResultProps) => void;
  baseMapStyle: Record<string, any>;
  height: string;
  width: string;
  minZoom?: number;
  account: Account;
  viewport: Viewport;
  setViewport: (viewport: Viewport, trigger?: any) => void;
  disableInteraction?: boolean;
  addressOnly?: boolean;
  halfMap?: boolean;
} & ComponentProps<typeof MapGL>;

export default React.forwardRef((props: Props, ref: ForwardedRef<MapRef>) => {
  const controller = new CustomMapController();
  const mapRef = (ref || useRef(null)) as MutableRefObject<MapRef>;

  const hookBuilders = Object.entries(props.layers).map(([layerId, config]) => {
    const layerHookBuilder = layerHookBuilders[
      layerId as ParentLayerNames
    ] as LayerHookBuilder<typeof config>;
    const builder: CurriedHookBuilder = callback =>
      callback(layerHookBuilder, config);
    return builder;
  });

  const {
    onClick,
    onHover,
    onLoad,
    onFocusLost,
    tooltip,
    layers,
    cursor,
    interactiveLayerIds,
    onLoadError,
  } = useLayeredMapHook({
    account: props.account,
    hookBuilders,
    map: mapRef,
  });

  return (
    <>
      <MapGL
        ref={mapRef}
        getCursor={() => cursor}
        mapboxApiAccessToken={window.env.MAPBOX_ACCESS_TOKEN}
        {...props.viewport}
        {...props}
        interactiveLayerIds={interactiveLayerIds}
        attributionControl={false}
        reuseMaps={false}
        onClick={onClick}
        onHover={onHover}
        onMouseOut={onFocusLost}
        controller={controller}
        onLoad={onLoad}
        mapStyle={props.baseMapStyle}
        onError={(e: any) => {
          if (e.error.status === 401) {
            window.location.href = buildLoginURLWithPrevLocation(location);
          } else if (e.error.status === 503) {
            onLoadError(e);
          }
        }}
        onViewportChange={
          !props.disableInteraction ? props.setViewport : () => {}
        }
      >
        {layers}
        {props.children}
        <TooltipComponent tooltip={tooltip} />
      </MapGL>
      {props.onSearchResult && (
        <SearchContainer halfMap={props.halfMap ?? false}>
          <Search
            handleResultClick={props.onSearchResult}
            addressOnly={props.addressOnly}
          />
        </SearchContainer>
      )}
    </>
  );
});

export * from "./types";
